import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { FileSpreadsheet, CheckCircle, Plus } from 'lucide-react';
import type { WorksheetMapping } from '../types/templateTypes';

interface DetectedSheet {
  name: string;
  rowCount: number;
}

/**
 * Commission template structure (compatible with shared-schemas CommissionTemplate)
 * Used for displaying and selecting templates in the admin UI
 */
interface CommissionTemplate {
  id: string;
  companyId: string;
  officeId?: string;
  name: string;
  description?: string;
  lenderName?: string;
  isActive: boolean;
  isDefault?: boolean;
  fileFormat: 'csv' | 'xlsx' | 'xls';
  hasHeaderRow: boolean;
  startRow: number;
  sheetName?: string;
  delimiter?: string;
  encoding?: string;
  isMultiSheet?: boolean;
  worksheetMapping?: WorksheetMapping;
  usageCount: number;
  lastUsedAt?: any;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
  updatedBy: string;
}

interface TemplateSelectorProps {
  companyId?: string;
  isMultiSheet: boolean;
  detectedSheets: DetectedSheet[];
  onTemplateSelect: (templateId: string) => void;
  onCreateNew: () => void;
  selectedTemplateId?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  companyId,
  isMultiSheet,
  detectedSheets,
  onTemplateSelect,
  onCreateNew,
  selectedTemplateId
}) => {
  const [templates, setTemplates] = useState<CommissionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use getTemplates cloud function (bypasses Firestore rules, requires isFounder).
        // Scope to this tenant's templates when a companyId is known; always drop
        // demo/sample templates. companyId may be undefined (serialized away) in
        // pre-company contexts, in which case the full non-demo catalogue returns.
        const getTemplatesFn = httpsCallable(functions, 'getTemplates');
        const result = await getTemplatesFn({ companyId, excludeDemo: true }) as any;

        if (result.data.success) {
          const fetchedTemplates = result.data.templates.map((t: any) => ({
            id: t.id,
            name: t.name || t.templateName,
            isMultiSheet: t.isMultiSheet || false,
            isActive: t.status === 'active' || t.isActive,
            fileFormat: t.fileFormat,
            ...t
          } as CommissionTemplate));

          // Filter templates based on file type compatibility
          const compatibleTemplates = fetchedTemplates.filter((template: CommissionTemplate) => {
            // Only show multi-sheet templates if file is multi-sheet
            if (isMultiSheet) {
              return template.isMultiSheet === true && template.isActive;
            }
            // For single-sheet files, show non-multi-sheet templates
            return !template.isMultiSheet && template.isActive;
          });

          setTemplates(compatibleTemplates);

          // Auto-select first compatible template if none selected
          if (!selectedTemplateId && compatibleTemplates.length > 0) {
            onTemplateSelect(compatibleTemplates[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setError('Could not load file templates.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [isMultiSheet, companyId, selectedTemplateId, onTemplateSelect]);

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-slate-700 rounded"></div>
            <div className="h-16 bg-slate-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
      {/* Header */}
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-white mb-2">
          {isMultiSheet ? 'Multi-Sheet File Detected' : 'Select Template'}
        </h4>

        {isMultiSheet && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-md p-3 mb-4">
            <p className="text-sm text-blue-300 mb-2">
              Detected {detectedSheets.length} worksheets in file:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {detectedSheets.map((sheet, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                  <FileSpreadsheet className="w-3 h-3 text-blue-400" />
                  <span className="font-medium">{sheet.name}</span>
                  <span className="text-slate-500">({sheet.rowCount} rows)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-sm text-slate-400">
          {templates.length === 0
            ? `No compatible ${isMultiSheet ? 'multi-sheet' : 'single-sheet'} templates found. Create a new template.`
            : `Select a compatible template or create a new one.`
          }
        </p>
      </div>

      {/* Template List */}
      {templates.length > 0 && (
        <div className="space-y-2 mb-4">
          {templates.map(template => (
            <div
              key={template.id}
              onClick={() => onTemplateSelect(template.id)}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedTemplateId === template.id
                  ? 'border-fuchsia-500 bg-fuchsia-500/10'
                  : 'border-slate-700 hover:border-slate-600 bg-slate-900/50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                    <span className="font-semibold text-white">{template.name}</span>
                    {template.isMultiSheet && (
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium">
                        Multi-Sheet
                      </span>
                    )}
                  </div>

                  {template.description && (
                    <p className="text-xs text-slate-400 mb-2">{template.description}</p>
                  )}

                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {template.lenderName && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                        {template.lenderName}
                      </span>
                    )}
                    <span className="uppercase">{template.fileFormat}</span>
                    {template.usageCount > 0 && (
                      <span>{template.usageCount} uploads</span>
                    )}
                  </div>

                  {/* Worksheet Mapping Preview */}
                  {template.isMultiSheet && template.worksheetMapping && (
                    <div className="mt-2 pt-2 border-t border-slate-700">
                      <p className="text-xs font-medium text-slate-400 mb-1">Worksheet Mapping:</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {template.worksheetMapping.upfront && (
                          <div className="text-slate-300">
                            <span className="text-green-400">Upfront:</span> {template.worksheetMapping.upfront}
                          </div>
                        )}
                        {template.worksheetMapping.trail && (
                          <div className="text-slate-300">
                            <span className="text-blue-400">Trail:</span> {template.worksheetMapping.trail}
                          </div>
                        )}
                        {template.worksheetMapping.nil && (
                          <div className="text-slate-300">
                            <span className="text-yellow-400">Nil:</span> {template.worksheetMapping.nil}
                          </div>
                        )}
                        {template.worksheetMapping.summary && (
                          <div className="text-slate-300">
                            <span className="text-purple-400">Summary:</span> {template.worksheetMapping.summary}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {selectedTemplateId === template.id && (
                  <CheckCircle className="w-5 h-5 text-fuchsia-400 flex-shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create New Template Button */}
      <button
        onClick={onCreateNew}
        className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all flex items-center justify-center gap-2 font-medium"
      >
        <Plus className="w-4 h-4" />
        Create New {isMultiSheet ? 'Multi-Sheet' : ''} Template
      </button>

      {/* Compatibility Warning */}
      {templates.length === 0 && (
        <div className="mt-4 bg-yellow-900/20 border border-yellow-500/30 rounded-md p-3">
          <p className="text-xs text-yellow-400">
            {isMultiSheet
              ? 'Multi-sheet files require templates with worksheet mapping configuration.'
              : 'This file appears to be single-sheet. Multi-sheet templates will not be compatible.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
